import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Warning as WarningIcon,
  Event as EventIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import productService from '../../services/productService';
import purchaseOrderService from '../../services/purchaseOrderService';
import companySettingsService from '../../services/companySettingsService';

/**
 * Parse API date values. Spring/Jackson often sends LocalDate as [year, month, day] (1-based month),
 * or as "yyyy-MM-dd" strings — not only ISO strings.
 */
function parseExpiryDate(value) {
  if (value == null || value === '') return null;
  try {
    if (Array.isArray(value) && value.length >= 3) {
      const y = Number(value[0]);
      const m = Number(value[1]);
      const day = Number(value[2]);
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return null;
      const d = new Date(y, m - 1, day);
      return isValid(d) ? d : null;
    }
    if (typeof value === 'string') {
      const s = value.includes('T') ? value.split('T')[0] : value.slice(0, 10);
      const d = parseISO(s);
      return isValid(d) ? d : null;
    }
  } catch {
    return null;
  }
  return null;
}

/** MFD may be in `mfd` or `manufacturingDate`; values may be string or Jackson date array */
function getMfdRaw(row) {
  if (!row) return null;
  return row.mfd ?? row.manufacturingDate ?? null;
}

function getExpiryRaw(row) {
  if (!row) return null;
  return row.expiryDate ?? row.expiry_date ?? null;
}

/** Calendar days from today to expiry (start of day). Negative if already expired. Null if no date. */
function getDaysRemaining(row) {
  const d = parseExpiryDate(getExpiryRaw(row));
  if (!d) return null;
  const today = startOfDay(new Date());
  const exp = startOfDay(d);
  return Math.round((exp.getTime() - today.getTime()) / 86400000);
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isExpiredFromRow(row) {
  const d = parseExpiryDate(getExpiryRaw(row));
  if (!d) return false;
  return startOfDay(d) < startOfDay(new Date());
}

/** Not expired, expiry within next `days` from today (inclusive of today) */
function isExpiringWithinDaysFromRow(row, days = 30) {
  const raw = getExpiryRaw(row);
  if (isExpiredFromRow(row)) return false;
  const d = parseExpiryDate(raw);
  if (!d) return false;
  const today = startOfDay(new Date());
  const limit = new Date(today);
  limit.setDate(limit.getDate() + days);
  return startOfDay(d) <= limit;
}

function expiryNoteLabel(row) {
  if (isExpiredFromRow(row)) return 'Expired';
  const days = getDaysRemaining(row);
  if (days === null) return '—';
  if (days < 0) return 'Expired';
  if (days === 0) return '0 days';
  return `${days} day${days === 1 ? '' : 's'}`;
}

function formatDisplayDate(value) {
  const d = parseExpiryDate(value);
  if (!d) return '—';
  try {
    return format(d, 'dd/MM/yyyy');
  } catch {
    return String(value);
  }
}

function formatMoney(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const BatchReport = () => {
  /** null = still loading company settings */
  const [enableBatchWise, setEnableBatchWise] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      companySettingsService
        .getSettings()
        .then((data) => {
          if (!cancelled) setEnableBatchWise(Boolean(data?.enableBatchWise ?? false));
        })
        .catch(() => {
          if (!cancelled) setEnableBatchWise(false);
        });
    };
    load();
    const onCompany = () => load();
    window.addEventListener('companyInfoUpdated', onCompany);
    return () => {
      cancelled = true;
      window.removeEventListener('companyInfoUpdated', onCompany);
    };
  }, []);

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productId, setProductId] = useState('');
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const selectedProduct = useMemo(
    () => products.find((p) => String(p.id) === String(productId)),
    [products, productId]
  );

  const summary = useMemo(() => {
    if (!productId) {
      return { total: 0, expired: 0, expiringSoon: 0 };
    }
    const total = batches.length;
    const expired = batches.filter((b) => isExpiredFromRow(b)).length;
    const expiringSoon = batches.filter(
      (b) => !isExpiredFromRow(b) && isExpiringWithinDaysFromRow(b, 30)
    ).length;
    return { total, expired, expiringSoon };
  }, [batches, productId]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await productService.getCategories({ size: 2000 });
        const list = res.content || res || [];
        setCategories(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load categories');
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (!categoryId) {
      setProducts([]);
      setProductId('');
      setBatches([]);
      return;
    }
    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        const res = await productService.getProductsByCategory(
          categoryId,
          0,
          5000,
          'name',
          'asc'
        );
        const list = Array.isArray(res) ? res : res?.content || res?.data || [];
        setProducts(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load products for category');
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };
    loadProducts();
  }, [categoryId]);

  const loadBatches = useCallback(async (pid) => {
    if (!pid) {
      setBatches([]);
      return;
    }
    setBatchesLoading(true);
    try {
      const data = await purchaseOrderService.getProductBatchesByProduct(Number(pid), null, true);
      const list = Array.isArray(data) ? data : [];
      setBatches(list);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load batches');
      setBatches([]);
    } finally {
      setBatchesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!productId) {
      setBatches([]);
      return;
    }
    loadBatches(productId);
  }, [productId, loadBatches]);

  const handleCategoryChange = (e) => {
    setCategoryId(e.target.value);
    setProductId('');
    setBatches([]);
  };

  const handleExportExcel = () => {
    if (!selectedProduct || !productId) {
      toast.error('Select a product first');
      return;
    }
    try {
      setExporting(true);
      const wb = XLSX.utils.book_new();
      const catLabel = categories.find((c) => String(c.id) === String(categoryId))?.name ?? '';

      const header = [
        'S.No',
        'Batch No',
        'MFD',
        'Expiry',
        'MRP',
        'Selling',
        'Purchase',
        'Qty',
        'Location',
        'Status',
        'Expiry note',
      ];

      const dataRows = batches.map((b, idx) => [
        idx + 1,
        b.batchNumber ?? '',
        formatDisplayDate(getMfdRaw(b)),
        formatDisplayDate(getExpiryRaw(b)),
        b.mrp != null ? Number(b.mrp) : '',
        b.sellingPrice != null ? Number(b.sellingPrice) : '',
        b.purchasePrice != null ? Number(b.purchasePrice) : '',
        b.quantity != null ? Number(b.quantity) : '',
        b.locationName ?? '',
        b.status ?? 'ACTIVE',
        expiryNoteLabel(b),
      ]);

      const aoa = [
        ['Batch Report'],
        ['Generated', new Date().toLocaleString()],
        ['Category', catLabel],
        ['Product', selectedProduct.name ?? ''],
        ['SKU', selectedProduct.sku ?? ''],
        [],
        ['Summary'],
        ['Total batches', summary.total],
        ['Expired batches', summary.expired],
        ['Expiring within 30 days', summary.expiringSoon],
        [],
        header,
        ...dataRows,
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [
        { wch: 8 },
        { wch: 14 },
        { wch: 12 },
        { wch: 12 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 20 },
        { wch: 10 },
        { wch: 22 },
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'Batch Report');
      const safeName = (selectedProduct.name || 'product').replace(/[\\/*?:[\]]/g, '').slice(0, 40);
      XLSX.writeFile(wb, `batch-report-${safeName}-${Date.now()}.xlsx`);
      toast.success('Excel downloaded');
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const categoryName = categories.find((c) => String(c.id) === String(categoryId))?.name ?? '';

  if (enableBatchWise === null) {
    return (
      <Layout>
        <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </Layout>
    );
  }

  if (!enableBatchWise) {
    return <Navigate to="/reports/products" replace />;
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Batch Report
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a category and product to list all batches with pricing and expiry, same as the product batch
          details screen.
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={categoryId}
                  onChange={handleCategoryChange}
                >
                  <MenuItem value="">
                    <em>Select category</em>
                  </MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={!categoryId || productsLoading}>
                <InputLabel>Product</InputLabel>
                <Select
                  label="Product"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select product</em>
                  </MenuItem>
                  {products.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                      {p.sku ? ` (${p.sku})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                disabled={!productId || exporting || batchesLoading}
                onClick={handleExportExcel}
                fullWidth
              >
                Export Excel
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {productId && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <InventoryIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Total batches
                    </Typography>
                  </Box>
                  <Typography variant="h4">{summary.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderColor: 'error.light' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <WarningIcon color="error" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Expired batches
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="error.main">
                    {summary.expired}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderColor: 'warning.light' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EventIcon color="warning" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Expiring in 30 days
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="warning.dark">
                    {summary.expiringSoon}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {productId && selectedProduct && (
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {selectedProduct.name}
            {categoryName ? ` · ${categoryName}` : ''}
          </Typography>
        )}

        {productId && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Batch No</TableCell>
                  <TableCell>MFD</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell align="right">MRP</TableCell>
                  <TableCell align="right">Selling</TableCell>
                  <TableCell align="right">Purchase</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expiry note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchesLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <CircularProgress size={28} sx={{ my: 2 }} />
                    </TableCell>
                  </TableRow>
                ) : batches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No batches for this product
                    </TableCell>
                  </TableRow>
                ) : (
                  batches.map((row) => {
                    const days = getDaysRemaining(row);
                    const expired =
                      isExpiredFromRow(row) || (days !== null && days < 0);
                    const soon =
                      !expired && days !== null && days >= 0 && days <= 30;
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.batchNumber}</TableCell>
                        <TableCell>{formatDisplayDate(getMfdRaw(row))}</TableCell>
                        <TableCell>{formatDisplayDate(getExpiryRaw(row))}</TableCell>
                        <TableCell align="right">{formatMoney(row.mrp)}</TableCell>
                        <TableCell align="right">{formatMoney(row.sellingPrice)}</TableCell>
                        <TableCell align="right">{formatMoney(row.purchasePrice)}</TableCell>
                        <TableCell align="right">
                          {row.quantity != null ? String(row.quantity) : '—'}
                        </TableCell>
                        <TableCell>{row.locationName ?? '—'}</TableCell>
                        <TableCell>{row.status || 'ACTIVE'}</TableCell>
                        <TableCell>
                          {expired && (
                            <Chip size="small" label="Expired" color="error" />
                          )}
                          {!expired && days !== null && (
                            <Chip
                              size="small"
                              label={
                                days === 0
                                  ? '0 days'
                                  : `${days} day${days === 1 ? '' : 's'}`
                              }
                              color={soon ? 'warning' : 'success'}
                              variant={soon ? 'filled' : 'outlined'}
                            />
                          )}
                          {!expired && days === null && (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Layout>
  );
};

export default BatchReport;
